import { RefreshCw, LogOut, Clock, Waves } from "lucide-react";
import { Button } from "@/components/ui/button";
import { classifyIncident } from "@/lib/incident";

const STATUS = {
  normal:   { label: "Normal",   color: "#00E676", glow: "rgba(0,230,118,0.55)" },
  warning:  { label: "Alerta",   color: "#FFD600", glow: "rgba(255,214,0,0.55)" },
  critical: { label: "Crítico",  color: "#FF9500", glow: "rgba(255,149,0,0.55)" },
  overflow: { label: "Desborde", color: "#FF3B30", glow: "rgba(255,59,48,0.6)" },
  low:      { label: "Bajo",     color: "#60A5FA", glow: "rgba(96,165,250,0.5)" },
};

const SensorCard = ({ sensor, index }) => {
  const st = STATUS[sensor.status] || STATUS.normal;
  const level = typeof sensor.current_level === "number" ? sensor.current_level : 0;
  const isOffline = sensor.connection_status === "offline";
  const incident = isOffline ? classifyIncident(sensor) : null;

  // Nivel de llenado para la visualización (min -> desborde)
  const min = sensor.min_threshold ?? 0;
  const overflow = sensor.overflow_level || sensor.max_threshold || (min + 100);
  const fill = Math.max(4, Math.min(100, ((level - min) / (overflow - min)) * 100));

  const updated = sensor.last_updated
    ? new Date(sensor.last_updated).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", timeZone: "Atlantic/Canary" })
    : null;

  return (
    <div
      className="visor-card relative h-full overflow-hidden rounded-[28px] border border-white/10 bg-obsidian-surface shadow-[0_8px_40px_rgba(0,0,0,0.45)]"
      style={{ animationDelay: `${index * 120}ms` }}
      data-testid={`visor-card-${sensor.name}`}
    >
      {/* Relleno de agua */}
      <div
        className="visor-fill absolute inset-x-0 bottom-0"
        style={{
          height: `${fill}%`,
          background: "linear-gradient(to top, rgba(10,132,255,0.55) 0%, rgba(0,160,255,0.38) 55%, rgba(0,229,255,0.22) 100%)",
          opacity: isOffline ? 0.12 : 1,
        }}
      >
        {/* Línea de superficie con color de estado */}
        <div
          className="visor-wave absolute -top-[1px] left-0 h-[3px] w-[200%]"
          style={{ background: st.color, boxShadow: `0 0 16px ${st.glow}` }}
        />
      </div>

      {/* Contenido */}
      <div className="relative z-10 flex h-full flex-col justify-between p-5 sm:p-6">
        {/* Cabecera: nombre + estado */}
        <div className="flex items-center justify-between">
          <span className="font-heading text-base sm:text-lg font-black uppercase tracking-[0.18em] text-white/90">
            {sensor.name}
          </span>
          <span
            className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider backdrop-blur-sm"
            style={{ color: st.color, backgroundColor: `${st.color}1A`, border: `1px solid ${st.color}55` }}
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: st.color, boxShadow: `0 0 8px ${st.glow}` }} />
            {isOffline ? "Sin conexión" : st.label}
          </span>
        </div>

        {/* Medida principal */}
        <div className="flex flex-1 flex-col items-center justify-center py-3">
          <div className="flex items-end gap-1.5">
            <span
              className="font-mono text-6xl sm:text-7xl font-black leading-none tabular-nums text-white"
              style={{ textShadow: "0 3px 18px rgba(0,0,0,0.6)" }}
              data-testid={`visor-level-${sensor.name}`}
            >
              {isOffline ? "--" : level.toFixed(2)}
            </span>
            <span className="mb-1.5 text-sm font-semibold uppercase tracking-widest text-white/60">m</span>
          </div>

          {/* Motivo de la incidencia (nunca se muestra un valor inventado) */}
          {isOffline && incident && (
            <div
              className="mt-4 flex flex-col items-center gap-1.5 px-2 text-center"
              data-testid={`visor-incident-${sensor.name}`}
            >
              <span className="rounded-md border border-red-500/40 bg-red-500/15 px-2.5 py-1 text-[11px] font-black uppercase tracking-wider text-red-300">
                &#9888; {incident.code}
              </span>
              <span className="text-[11px] font-semibold text-amber-300/90">{incident.cause}</span>
            </div>
          )}
        </div>

        {/* Pie: actualización */}
        <div className="flex items-center justify-center gap-1.5 text-[10px] text-white/45">
          <Clock className="h-3 w-3" />
          {isOffline
            ? "Sin señal de la sonda"
            : updated
            ? `Actualizado ${updated}`
            : "Esperando datos"}
        </div>
      </div>
    </div>
  );
};

const VisorView = ({ sensors, currentTime, onRefresh, onLogout, refreshing }) => {
  const order = ["PARRALILLO", "SIBERIO", "CAIDERO"];
  const ordered = order
    .map((name) => sensors.find((s) => s.name === name))
    .filter(Boolean);

  return (
    <div className="visor-bg min-h-screen noise-bg" data-testid="dashboard-container">
      {/* Cabecera */}
      <header
        className="glass sticky top-0 z-20 flex items-center justify-between border-b border-white/10 px-4 sm:px-6"
        style={{ height: "58px" }}
        data-testid="visor-header"
      >
        <div className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_65caa12a-9cef-4fb1-b65d-555171978c75/artifacts/uc0sdgit_LOGO%20FINAL%20LIVEWIFI%20BLANCO.png"
            alt="LIVEWIFI"
            className="h-5 w-auto object-contain"
          />
          <div className="flex items-center">
            <h1 className="font-heading text-base font-black uppercase tracking-tight text-white">Centinela</h1>
            <span className="ml-2 rounded-full border border-water-flow/30 bg-water-flow/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-water-flow">
              Visor
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <span className="mr-1 hidden items-center gap-1.5 font-mono text-sm tabular-nums text-white/80 sm:flex">
            <Clock className="h-4 w-4 text-water-flow/60" />
            {currentTime.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", timeZone: "Atlantic/Canary" })}
          </span>
          <Button
            variant="ghost" size="icon" onClick={onRefresh} disabled={refreshing}
            className="h-9 w-9 text-zinc-400 hover:text-white" data-testid="visor-refresh-btn"
          >
            <RefreshCw className={`h-5 w-5 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
          <Button
            variant="ghost" size="icon" onClick={onLogout}
            className="h-9 w-9 text-zinc-400 hover:text-red-400" data-testid="visor-logout-btn"
          >
            <LogOut className="h-5 w-5" />
          </Button>
        </div>
      </header>

      {/* Contenido */}
      <main className="mx-auto w-full max-w-5xl px-4 py-5 sm:px-6 sm:py-7" data-testid="visor-sensors">
        <div className="mb-4 flex items-center gap-2">
          <Waves className="h-4 w-4 text-water-flow/70" />
          <h2 className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-white/70">
            Niveles de las Sondas
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-5">
          {ordered.map((sensor, i) => (
            <div key={sensor.id} className="h-[30vh] min-h-[250px] sm:h-[68vh] sm:min-h-[440px]">
              <div className="h-full">
                <SensorCard sensor={sensor} index={i} />
              </div>
            </div>
          ))}
        </div>

        {ordered.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-obsidian-surface py-16 text-center text-white/50">
            Esperando datos de las sondas…
          </div>
        )}
      </main>
    </div>
  );
};

export default VisorView;
